
import { useState, useEffect } from 'react';
import { Instagram, Youtube, Eye, Video } from 'lucide-react';
import { 
  fetchLatestYouTubeVideos,
  YouTubeVideo,
  formatViewCount,
  formatPublishDate 
} from '@/lib/youtube';

type FeedType = 'instagram' | 'youtube';

const SocialFeed = () => {
  const [activeFeed, setActiveFeed] = useState<FeedType>('youtube'); // Default is YouTube
  const [loading, setLoading] = useState(true);
  const [youtubeVideos, setYoutubeVideos] = useState<YouTubeVideo[]>([]);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    const fetchYouTubeVideos = async () => {
      setLoading(true);
      setError(false);
      try {
        const videos = await fetchLatestYouTubeVideos(2);
        setYoutubeVideos(videos);
      } catch (error) {
        console.error('Error fetching YouTube videos in SocialFeed:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchYouTubeVideos();
  }, []);

  return (
    <div className="glass-card rounded-lg overflow-hidden">
      <div className="flex border-b border-adv-darkGray">
        <button
          onClick={() => setActiveFeed('youtube')}
          className={`flex items-center justify-center flex-1 py-4 text-sm font-medium transition-colors ${
            activeFeed === 'youtube'
              ? 'text-adv-orange border-b-2 border-adv-orange'
              : 'text-adv-lightGray hover:text-white'
          }`}
        >
          <Youtube size={18} className="mr-2" /> YouTube
        </button>
        <button
          onClick={() => setActiveFeed('instagram')}
          className={`flex items-center justify-center flex-1 py-4 text-sm font-medium transition-colors ${
            activeFeed === 'instagram'
              ? 'text-adv-orange border-b-2 border-adv-orange'
              : 'text-adv-lightGray hover:text-white'
          }`}
        >
          <Instagram size={18} className="mr-2" /> Instagram
        </button>
      </div>

      <div className="p-4">
        {activeFeed === 'instagram' ? (
          <div className="text-center py-6">
            <Instagram size={32} className="text-adv-lightGray mx-auto mb-3" />
            <p className="text-adv-lightGray">Follow @ExodusADV on Instagram for updates.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {loading ? (
              Array(2).fill(0).map((_, i) => (
                <div key={i} className="rounded-lg bg-adv-darkGray/20 animate-pulse">
                  <div className="aspect-video rounded-lg mb-2"></div>
                  <div className="h-4 bg-adv-darkGray/20 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-adv-darkGray/20 rounded w-1/2"></div>
                </div>
              ))
            ) : error ? (
              <div className="text-center py-6">
                <Video size={32} className="text-adv-lightGray mx-auto mb-3" />
                <p className="text-adv-lightGray">Videos coming soon. Subscribe to @ExodusADV on YouTube.</p>
              </div>
            ) : youtubeVideos.length > 0 ? (
              youtubeVideos.map((video) => (
                <a
                  key={video.id}
                  href={`https://www.youtube.com/watch?v=${video.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block group"
                >
                  <div className="relative overflow-hidden rounded-lg aspect-video mb-2">
                    <img
                      src={video.thumbnail.url}
                      alt={video.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-red-600/80 rounded-full p-2 transform transition-all duration-300 group-hover:scale-110 group-hover:bg-red-600">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    
                    {video.statistics?.viewCount && (
                      <div className="absolute bottom-2 right-2 bg-black/70 backdrop-blur-sm rounded-md px-2 py-0.5 flex items-center">
                        <Eye size={12} className="text-white mr-1" />
                        <span className="text-white text-xs">
                          {formatViewCount(video.statistics.viewCount)}
                        </span>
                      </div>
                    )}
                  </div>
                  <h4 className="text-white font-medium group-hover:text-adv-orange transition-colors line-clamp-2">
                    {video.title}
                  </h4>
                  <div className="flex items-center text-xs text-adv-lightGray mt-1">
                    <span>{formatPublishDate(video.publishedAt)}</span>
                  </div>
                </a>
              ))
            ) : (
              <div className="text-center py-6">
                <Video size={32} className="text-adv-lightGray mx-auto mb-3" />
                <p className="text-adv-lightGray">Videos coming soon. Subscribe to @ExodusADV on YouTube.</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="p-4 border-t border-adv-darkGray text-center">
        <a
          href={activeFeed === 'instagram' ? 'https://instagram.com/ExodusADV' : 'https://youtube.com/@Exodusadv'}
          target="_blank"
          rel="noopener noreferrer"
          className="text-adv-orange hover:text-adv-orange/80 text-sm font-medium inline-flex items-center"
        >
          View {activeFeed === 'instagram' ? 'Instagram' : 'YouTube'} Profile
          <svg className="ml-1 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </div>
    </div>
  );
};

export default SocialFeed;
